import React, { useState } from 'react';
import { useLocation } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { TrustBadge } from '../components/brand/TrustBadge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Sparkles, Loader2, Wand2, Copy, Check } from 'lucide-react';
import { toast } from 'sonner';
import { DashboardShell } from '../components/brand/DashboardShell';
import { clinicianNavItems } from '../components/brand/navItems';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AIPracticeCoachPage() {
  const { token } = useAuth();
  const location = useLocation();
  const [tab, setTab] = useState('bio');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState(false);

  // Bio inputs
  const [licenseType, setLicenseType] = useState('RN');
  const [years, setYears] = useState(5);
  const [specialties, setSpecialties] = useState('post-discharge, chronic care');
  const [city, setCity] = useState('');
  const [passion, setPassion] = useState('');

  const run = async (action) => {
    setLoading(true); setResult(null); setCopied(false);
    try {
      const { data } = await axios.post(`${API}/ai/practice-coach`,
        { action, inputs: { license_type: licenseType, years, specialties, city, passion } },
        { headers: { Authorization: `Bearer ${token}` } });
      setResult(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'AI failed — try again');
    } finally { setLoading(false); }
  };

  const copy = (text) => {
    navigator.clipboard.writeText(text);
    setCopied(true); toast.success('Copied');
    setTimeout(() => setCopied(false), 1500);
  };

  const savePackage = async (p) => {
    try {
      await axios.post(`${API}/clinician/packages`, p, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`Added "${p.name}" to your profile`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not save package');
    }
  };

  return (
    <DashboardShell
      navItems={clinicianNavItems({ pathname: location.pathname })}
      title="Practice Coach"
      subtitle="Draft a bio, headline, and service packages tuned for the CaraLoom marketplace — review and edit before publishing."
      testId="practice-coach-page"
    >
      <main className="max-w-3xl">
        <div className="flex gap-2 mb-6">
          {[
            { id: 'bio', label: 'Draft my bio' },
            { id: 'headline', label: 'Draft headline' },
            { id: 'packages', label: 'Suggest packages' },
          ].map((t) => (
            <button key={t.id} onClick={() => { setTab(t.id); setResult(null); }}
              data-testid={`coach-tab-${t.id}`}
              className={`px-4 py-2 rounded-full text-sm font-semibold ${tab === t.id ? 'bg-cn-primary text-white' : 'bg-white border border-cn-border text-cn-text hover:bg-cn-bone-100'}`}>
              {t.label}
            </button>
          ))}
        </div>

        <div className="cn-card p-6 space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs font-bold text-cn-text-muted uppercase tracking-wider">License</Label>
              <Input value={licenseType} onChange={(e) => setLicenseType(e.target.value)} className="mt-1.5 h-11 rounded-xl bg-white border-cn-border" data-testid="coach-license" />
            </div>
            <div>
              <Label className="text-xs font-bold text-cn-text-muted uppercase tracking-wider">Years experience</Label>
              <Input type="number" value={years} onChange={(e) => setYears(e.target.value)} className="mt-1.5 h-11 rounded-xl bg-white border-cn-border" data-testid="coach-years" />
            </div>
          </div>
          <div>
            <Label className="text-xs font-bold text-cn-text-muted uppercase tracking-wider">Specialties (comma-separated)</Label>
            <Input value={specialties} onChange={(e) => setSpecialties(e.target.value)} className="mt-1.5 h-11 rounded-xl bg-white border-cn-border" data-testid="coach-specialties" />
          </div>
          {tab === 'headline' && (
            <div>
              <Label className="text-xs font-bold text-cn-text-muted uppercase tracking-wider">City</Label>
              <Input value={city} onChange={(e) => setCity(e.target.value)} placeholder="Brooklyn" className="mt-1.5 h-11 rounded-xl bg-white border-cn-border" data-testid="coach-city" />
            </div>
          )}
          {tab === 'bio' && (
            <div>
              <Label className="text-xs font-bold text-cn-text-muted uppercase tracking-wider">What drew you to care work?</Label>
              <Input value={passion} onChange={(e) => setPassion(e.target.value)} placeholder="Helping families navigate aging at home..." className="mt-1.5 h-11 rounded-xl bg-white border-cn-border" data-testid="coach-passion" />
            </div>
          )}
          <button onClick={() => run(tab === 'bio' ? 'draft_bio' : tab === 'headline' ? 'draft_headline' : 'suggest_packages')}
            disabled={loading} className="cn-btn cn-btn-primary w-full" data-testid="coach-run-button">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Wand2 className="w-4 h-4" /> Generate</>}
          </button>
        </div>

        {result && (
          <div className="cn-card p-6 mt-6" data-testid="coach-result">
            <div className="flex items-center gap-2 mb-3">
              <Sparkles className="w-4 h-4 text-cn-terracotta-500" />
              <span className="font-display font-bold text-lg">Your draft</span>
              <TrustBadge variant="ai-confidence" label="GPT-5.2" className="ml-auto" />
            </div>
            {result.text && (
              <div className="space-y-3">
                <p className="text-cn-text leading-relaxed whitespace-pre-line bg-cn-bone-100 rounded-2xl p-4">{result.text}</p>
                <button onClick={() => copy(result.text)} className="cn-btn cn-btn-ghost text-sm" data-testid="coach-copy">
                  {copied ? <><Check className="w-4 h-4 text-cn-primary" /> Copied</> : <><Copy className="w-4 h-4" /> Copy</>}
                </button>
              </div>
            )}
            {result.packages && (
              <div className="space-y-3">
                {result.packages.map((p, i) => (
                  <div key={i} className="rounded-2xl border border-cn-border bg-cn-bone-100 p-4" data-testid={`coach-pkg-${i}`}>
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <div className="font-display font-bold">{p.name}</div>
                        <div className="text-xs text-cn-text-muted mt-0.5">{p.service_type} · {p.duration_hours}h · ${p.price}{p.visits_included > 1 ? ` · ${p.visits_included} visits` : ''}</div>
                        {p.description && <p className="text-sm text-cn-text mt-2">{p.description}</p>}
                      </div>
                      <button onClick={() => savePackage(p)} className="cn-btn cn-btn-primary text-xs flex-shrink-0" data-testid={`coach-add-pkg-${i}`}>
                        Add to profile
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        <p className="text-xs text-cn-text-muted mt-6">CaraLoom Practice Coach uses AI to draft suggestions — review and edit before publishing. You stay in control of your profile.</p>
      </main>
    </DashboardShell>
  );
}
